import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, ArrowLeft, ChevronDown, ChevronRight, Eraser, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchCrosswordDaily, submitCrossword } from '../../services/gamesApiWave2';
import GameResultCard from './GameResultCard';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Bible Crossword — small daily 7x7 puzzle. Each cell is either blocked
 * or a letter cell. Tapping a letter cell focuses it; the active direction
 * (across/down) auto-toggles based on the cell's word membership and the
 * user's last typed key. Typing a letter advances within the current word.
 *
 * Backend supplies blocks + numbered cells + clues (no answers). On submit
 * the server compares each filled cell vs. the solution and returns the
 * list of wrong cells for inline marking.
 */
const k = (r, c) => `${r},${c}`;

const BibleCrosswordGame = ({ language = 'en', onBack, onScored }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cells, setCells] = useState({});           // {"r,c": "A"}
  const [active, setActive] = useState(null);       // [r, c]
  const [direction, setDirection] = useState('across');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    fetchCrosswordDaily(language)
      .then((d) => {
        setData(d);
        // Pick the first letter cell by default.
        const blocks = new Set(d.blocks.map(([r, c]) => k(r, c)));
        outer: for (let r = 0; r < d.size; r++) {
          for (let c = 0; c < d.size; c++) {
            if (!blocks.has(k(r, c))) {
              setActive([r, c]);
              break outer;
            }
          }
        }
      })
      .catch(() => toast.error(t('games.failedLoad')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const blockSet = useMemo(
    () => new Set((data?.blocks || []).map(([r, c]) => k(r, c))),
    [data],
  );

  const numberAt = useMemo(() => {
    const m = {};
    (data?.numbered || []).forEach((n) => { m[k(n.row, n.col)] = n.number; });
    return m;
  }, [data]);

  // Identify the word currently traversed by the active cell + direction
  // so we can highlight the entire word and advance focus along it.
  const activeWord = useMemo(() => {
    if (!data || !active) return [];
    const [ar, ac] = active;
    const path = [];
    if (direction === 'across') {
      let c = ac;
      while (c >= 0 && !blockSet.has(k(ar, c))) c--;
      c++;
      while (c < data.size && !blockSet.has(k(ar, c))) {
        path.push([ar, c]);
        c++;
      }
    } else {
      let r = ar;
      while (r >= 0 && !blockSet.has(k(r, ac))) r--;
      r++;
      while (r < data.size && !blockSet.has(k(r, ac))) {
        path.push([r, ac]);
        r++;
      }
    }
    return path;
  }, [data, active, direction, blockSet]);

  const wrongSet = useMemo(
    () => new Set((result?.wrong || []).map(([r, c]) => k(r, c))),
    [result],
  );

  const focusInput = () => { setTimeout(() => inputRef.current?.focus(), 0); };

  const moveActive = (delta) => {
    if (!active || activeWord.length === 0) return;
    const idx = activeWord.findIndex(([r, c]) => r === active[0] && c === active[1]);
    if (idx < 0) return;
    const next = idx + delta;
    if (next >= 0 && next < activeWord.length) setActive(activeWord[next]);
  };

  const onCellClick = (r, c) => {
    if (blockSet.has(k(r, c))) return;
    if (active && active[0] === r && active[1] === c) {
      setDirection((d) => (d === 'across' ? 'down' : 'across'));
    } else {
      setActive([r, c]);
    }
    focusInput();
  };

  const onKeyDown = (e) => {
    if (!active || result) return;
    const [r, c] = active;
    if (/^[a-zA-Z]$/.test(e.key)) {
      e.preventDefault();
      setCells((prev) => ({ ...prev, [k(r, c)]: e.key.toUpperCase() }));
      moveActive(1);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      setCells((prev) => {
        const ns = { ...prev };
        if (ns[k(r, c)]) {
          delete ns[k(r, c)];
        } else {
          // back up one cell and clear it
          const idx = activeWord.findIndex(([rr, cc]) => rr === r && cc === c);
          if (idx > 0) {
            const [pr, pc] = activeWord[idx - 1];
            delete ns[k(pr, pc)];
            setActive([pr, pc]);
          }
        }
        return ns;
      });
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const horizontal = e.key === 'ArrowRight' || e.key === 'ArrowLeft';
      setDirection(horizontal ? 'across' : 'down');
      const sign = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
      const dr = horizontal ? 0 : sign;
      const dc = horizontal ? sign : 0;
      const nr = r + dr;
      const nc = c + dc;
      if (nr >= 0 && nr < data.size && nc >= 0 && nc < data.size && !blockSet.has(k(nr, nc))) {
        setActive([nr, nc]);
      }
    } else if (e.key === ' ') {
      e.preventDefault();
      setDirection((d) => (d === 'across' ? 'down' : 'across'));
    }
  };

  const clearAll = () => setCells({});

  const onSubmit = async () => {
    if (!data) return;
    setSubmitting(true);
    try {
      const r = await submitCrossword(data.id, cells);
      setResult(r);
      onScored?.(r);
    } catch {
      toast.error(t('games.match.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.loading')}
      </div>
    );
  }
  if (!data) return null;

  const activeKey = active ? k(active[0], active[1]) : null;
  const wordSet = new Set(activeWord.map(([r, c]) => k(r, c)));

  return (
    <div className="space-y-4" data-testid={result ? 'crossword-result-screen' : 'crossword-game-screen'}>
      <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('games.back')}
      </Button>

      {/* Hidden input that captures keystrokes — keeps mobile keyboards open. */}
      <input
        ref={inputRef}
        className="sr-only"
        autoComplete="off"
        autoCapitalize="characters"
        onKeyDown={onKeyDown}
        readOnly
      />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">
          {t('games.crossword.title')}
        </h2>
        <Badge variant="outline" className="text-[10px]">
          {data.title} · {data.size}×{data.size}
        </Badge>
      </div>

      {result && (
        <GameResultCard
          language={language}
          gameTitle={t('games.crossword.title')}
          correct={result.correct_cells}
          total={result.total_cells}
          score={result.score}
          perfect={result.perfect}
          streak={result.streak}
          newTotal={result.new_total}
          subtitle={data.title}
        />
      )}

      {/* Grid */}
      <div
        className="inline-block bg-foreground/10 p-1 rounded mx-auto"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${data.size}, 38px)`, gap: 2 }}
        data-testid="crossword-grid"
      >
        {Array.from({ length: data.size * data.size }).map((_, i) => {
          const r = Math.floor(i / data.size);
          const c = i % data.size;
          const key = k(r, c);
          if (blockSet.has(key)) {
            return <div key={key} className="w-[38px] h-[38px] bg-foreground/80 rounded-sm" />;
          }
          const isActive = activeKey === key;
          const inWord = wordSet.has(key);
          const isWrong = wrongSet.has(key);
          const num = numberAt[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => onCellClick(r, c)}
              data-testid={`crossword-cell-${r}-${c}`}
              className={`relative w-[38px] h-[38px] text-base font-semibold flex items-center justify-center rounded-sm border-2 transition-colors ${
                isWrong
                  ? 'bg-red-500/20 border-red-500/70 text-red-700 dark:text-red-300'
                  : isActive
                    ? 'bg-purple-500/30 border-purple-600 text-foreground'
                    : inWord
                      ? 'bg-purple-500/10 border-purple-300 dark:border-purple-700 text-foreground'
                      : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
            >
              {num !== undefined && (
                <span className="absolute top-0 left-0.5 text-[8px] leading-none font-mono opacity-70">
                  {num}
                </span>
              )}
              {cells[key] || ''}
            </button>
          );
        })}
      </div>

      {/* Clues */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="crossword-clues">
        {['across', 'down'].map((dir) => (
          <Card key={dir}>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2">
                {dir === 'across'
                  ? <ChevronRight className="w-3 h-3" />
                  : <ChevronDown className="w-3 h-3" />}
                {dir === 'across' ? t('games.crossword.across') : t('games.crossword.down')}
              </p>
              <ul className="space-y-1.5 text-xs">
                {(data.clues[dir] || []).map((cl) => {
                  const isActive = active && active[0] === cl.row && active[1] === cl.col && direction === dir;
                  return (
                    <li
                      key={cl.id}
                      onClick={() => { setActive([cl.row, cl.col]); setDirection(dir); focusInput(); }}
                      className={`cursor-pointer px-1 py-0.5 rounded ${
                        isActive ? 'bg-purple-500/20 text-foreground font-medium' : 'hover:bg-muted/50'
                      }`}
                      data-testid={`crossword-clue-${cl.id}`}
                    >
                      <span className="font-mono text-purple-600 mr-1.5">{cl.number}.</span>
                      {cl.clue} <span className="text-muted-foreground">({cl.length})</span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {!result && (
        <div className="flex gap-2">
          <Button onClick={clearAll} variant="outline" size="sm" data-testid="crossword-clear-btn">
            <Eraser className="w-3.5 h-3.5 mr-1.5" />
            {t('games.crossword.clear')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || Object.keys(cells).length === 0}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
            data-testid="crossword-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {t('games.crossword.check')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BibleCrosswordGame;
